<template>
  <div class="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
    <!-- Tabs -->
    <div class="flex border-b">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="flex-1 py-3 px-4 text-center transition-colors duration-200 focus:outline-none" 
        :class="activeTab === tab.id
          ? 'text-blue-600 border-b-2 border-blue-600 font-medium'
          : 'text-gray-500 hover:text-blue-600'"
        @click="activeTab = tab.id"
      >
        {{ tab.name }}
      </button>
    </div>

    <!-- Sport Filter Pills -->
    <div class="p-3">
      <div class="flex flex-wrap gap-2">
        <button
          class="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
          :class="(activeTab === 'results' ? selectedSportFilter : selectedForecastSportFilter) === 'all'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
          @click="setFilter('all')"
        >
          All Sports
        </button>
        <button
          v-for="sport in activeTab === 'results' ? availableSports : availableForecastSports"
          :key="sport"
          class="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
          :class="(activeTab === 'results' ? selectedSportFilter : selectedForecastSportFilter) === sport
            ? 'bg-blue-100 text-blue-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
          @click="setFilter(sport)"
        >
          {{ formatSport(sport) }}
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="p-3">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="currentError" class="p-3 bg-red-50 text-red-600 rounded-lg">
        {{ currentError }}
      </div>

      <!-- Content State -->
      <div v-else class="space-y-4">
        <!-- Sport Groups -->
        <div v-for="(group, sport) in currentGroupedData" :key="sport" class="space-y-2">
          <div class="flex items-center bg-blue-100 p-2 rounded">
            <img :src="getIcon(sport)" alt="Sport Icon" class="w-5 h-5" v-if="getIcon(sport)" />
            <h3 class="text-lg font-semibold capitalize text-gray-800 px-1">
              {{ formatSport(sport) }}
            </h3>
          </div>
          <ul class="space-y-2">
            <li
              v-for="(item, index) in group"
              :key="item.id || index"
              class="p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <!-- Result Item -->
              <div class="flex flex-col gap-1.5">
                <!-- Header with event name (или счет для results) и датой -->
                <div class="flex justify-between items-center">
                  <div class="text-sm font-medium text-gray-900 pr-2 pb-2">
                    <template v-if="activeTab === 'results' && item.scores && item.scores.length > 0">
                      <span class="inline-flex items-center">
                        <span class="text-[12px] max-w-[150px]">{{ item.scores[0].name }}</span>
                        <span class="mx-1 px-1.5 py-0.5 bg-gray-200 rounded font-bold text-xs">
                          {{ item.scores[0].score }}
                        </span>
                        <span class="mx-0.5">:</span>
                        <span class="px-1.5 py-0.5 bg-gray-200 rounded font-bold text-xs">
                          {{ item.scores[1].score }}
                        </span>
                        <span class="text-[12px] max-w-[150px] ml-1">{{ item.scores[1].name }}</span>
                      </span>
                    </template>
                    <template v-else>
                      {{ item.eventName }}
                    </template>
                  </div>
                  <div class="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                    {{ formatDateCompact(item.date) }}
                  </div>
                </div>
                
                <!-- Bottom row with forecast, coefficient and status -->
                <div class="flex items-center justify-between">
                  <div class="flex w-full items-center gap-2">
                    <div class="px-2 py-0.5 bg-blue-100 rounded text-xs font-medium text-gray-700">
                      {{ translateTerm(item.forecast) }}
                    </div>
                    <div class="ml-auto px-2 py-0.5 rounded text-xs font-bold"
                      :class="getCoefficientClass(item)">
                      {{ item.coefficient }}
                    </div>
                  </div>
                  <div v-if="activeTab === 'results' && item.finalStatus" 
                    class="text-[10px] px-1.5 py-0.5 rounded-sm font-medium"
                    :class="getStatusClass(item.finalStatus)">
                  
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Empty state -->
        <div
          v-if="Object.keys(currentGroupedData).length === 0"
          class="text-center py-6 text-gray-500"
        >
          No data available
        </div>

        <!-- Pagination -->
        <div class="flex justify-center items-center space-x-4 pt-2" v-if="totalPages > 1">
          <button 
            @click="prevPage" 
            :disabled="currentPage === 1"
            class="p-1.5 rounded-md text-gray-500 disabled:opacity-50 hover:bg-gray-100 transition-colors"
            :class="currentPage === 1 ? 'cursor-not-allowed' : 'cursor-pointer'">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span class="text-sm text-gray-600">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages"
            class="p-1.5 rounded-md text-gray-500 disabled:opacity-50 hover:bg-gray-100 transition-colors"
            :class="currentPage === totalPages ? 'cursor-not-allowed' : 'cursor-pointer'">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Вывод языка для проверки -->

</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

// Получаем параметр языка из маршрута
const route = useRoute();
const lang = computed(() => route.params.lang || 'de');

// Определяем вычисляемое свойство для вкладок, чтобы они обновлялись при изменении языка
const tabs = computed(() => [
  { id: 'results', name: t('results') },
  { id: 'forecast', name: t('forecast') }
]);
function getStatusClass(status) {
  if (!status) return 'bg-gray-100 text-gray-500';
  
  switch(status.toLowerCase()) {
    case 'finished':
      return 'bg-green-50 text-green-700';
    case 'inprogress':
      return 'bg-blue-50 text-blue-700';
    case 'postponed':
      return 'bg-amber-50 text-amber-700';
    case 'not_started':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-500';
  }
}
// Simple English translations для sportNames
const sportNames = {
  'esports_csgo': 'CS:GO',
  'esports_dota': 'Dota 2',
  'esports_lol': 'League of Legends',
  'basketball': 'Basketball',
  'soccer': 'Soccer',
  'icehockey': 'Ice Hockey',
  'baseball': 'Baseball',
  'american_football': 'American Football',
  'tennis': 'Tennis',
  'mma': 'MMA',
  'boxing': 'Boxing',
  'cricket': 'Cricket',
  'rugby': 'Rugby',
  'motorsport': 'Motorsport',
  'golf': 'Golf',
};

// Переводы для трёх языков: de, fr, kor.
const translations = {
  de: {
    results: "Ergebnisse",
    forecast: "Prognose",
    confidence: "Zuversicht",
    coefficient: "Quote",
    league: "Liga",
    date: "Datum",
    start_time: "Startzeit",
    betResult: "Wettergebnis",
    finalStatus: "Endstatus",
    scores: "Punkte",
    eventName: "Veranstaltungsname",
    description: "Beschreibung",
    sport: "Sport",
    HOME: "Heim",
    AWAY: "Auswärts",
    DRAW: "Unentschieden",
    "HOME HANDICAP": "Heim-Handicap",
    "AWAY HANDICAP": "Auswärts-Handicap",
    OVER: "Über",
    UNDER: "Unter",
    "HOME MONEYLINE": "Heim-Sieg",
    "AWAY MONEYLINE": "Auswärts-Sieg",
    "FULL TIME": "Vollzeit",
    won: "Gewonnen",
    lost: "Verloren",
    finished: "Beendet",
    not_started: "Nicht begonnen",
    inprogress: "Läuft",
    postponed: "Verschoben",
    esports_csgo: "CS:GO",
    esports_dota: "Dota 2",
    esports_lol: "League of Legends",
    basketball: "Basketball",
    soccer: "Fußball",
    icehockey: "Eishockey",
    baseball: "Baseball",
    american_football: "American Football",
    tennis: "Tennis",
    mma: "MMA",
    boxing: "Boxen",
    cricket: "Cricket",
    rugby: "Rugby",
    motorsport: "Motorsport",
    golf: "Golf",
  },
  fr: {
    results: "Résultats",
    forecast: "Pronostic",
    confidence: "Confiance",
    coefficient: "Cote",
    league: "Ligue",
    date: "Date",
    start_time: "Heure de début",
    betResult: "Résultat du pari",
    finalStatus: "Statut final",
    scores: "Scores",
    eventName: "Nom de l'événement",
    description: "Description",
    sport: "Sport",
    HOME: "Domicile",
    AWAY: "Extérieur",
    DRAW: "Match nul",
    "HOME HANDICAP": "Handicap Domicile",
    "AWAY HANDICAP": "Handicap Extérieur",
    OVER: "Plus de",
    UNDER: "Moins de",
    "HOME MONEYLINE": "Cote Domicile",
    "AWAY MONEYLINE": "Cote Extérieur",
    "FULL TIME": "Temps réglementaire",
    won: "Gagné",
    lost: "Perdu",
    finished: "Terminé",
    not_started: "Pas commencé",
    inprogress: "En cours",
    postponed: "Reporté",
    esports_csgo: "CS:GO",
    esports_dota: "Dota 2",
    esports_lol: "League of Legends",
    basketball: "Basket-ball",
    soccer: "Football",
    icehockey: "Hockey sur glace",
    baseball: "Base-ball",
    american_football: "Football américain",
    tennis: "Tennis",
    mma: "MMA",
    boxing: "Boxe",
    cricket: "Cricket",
    rugby: "Rugby",
    motorsport: "Sports mécaniques",
    golf: "Golf",
  },
  kor: {
    results: "결과",
    forecast: "예측",
    confidence: "신뢰도",
    coefficient: "배당",
    league: "리그",
    date: "날짜",
    start_time: "시작 시간",
    betResult: "베팅 결과",
    finalStatus: "최종 상태",
    scores: "점수",
    eventName: "이벤트 이름",
    description: "설명",
    sport: "스포츠",
    HOME: "홈",
    AWAY: "원정",
    DRAW: "무승부",
    "HOME HANDICAP": "핸디캡(홈)",
    "AWAY HANDICAP": "핸디캡(원정)",
    OVER: "오버",
    UNDER: "언더",
    "HOME MONEYLINE": "홈 승",
    "AWAY MONEYLINE": "원정 승",
    "FULL TIME": "정규 시간",
    won: "승",
    lost: "패",
    finished: "종료",
    not_started: "시작 전",
    inprogress: "진행 중",
    postponed: "연기됨",
    esports_csgo: "CS:GO",
    esports_dota: "Dota 2",
    esports_lol: "리그 오브 레전드",
    basketball: "농구",
    soccer: "축구",
    icehockey: "아이스하키",
    baseball: "야구",
    american_football: "미식축구",
    tennis: "테니스",
    mma: "종합 격투기",
    boxing: "복싱",
    cricket: "크리켓",
    rugby: "럭비",
    motorsport: "모터스포츠",
    golf: "골프",
  },
};

function t(key) {
  if (!key) return '';
  if (translations[lang.value] && translations[lang.value][key]) {
    return translations[lang.value][key];
  }
  return key;
}

function translateTerm(term) {
  if (!term) return '';
  let forecast = term.trim().toUpperCase();
  const removeRegexes = [
    /\(LIVE\)/gi,
    /\(Q1\)/gi,
    /\(1H\)/gi,
    /LIVE/gi,
    /\bQ1\b/gi,
    /\b1H\b/gi
  ];
  for (const re of removeRegexes) {
    forecast = forecast.replace(re, '').trim();
  }
  const fullTimeRegex = /\(FULL TIME\)/i;
  let hasFullTime = false;
  if (fullTimeRegex.test(forecast)) {
    forecast = forecast.replace(fullTimeRegex, '').trim();
    hasFullTime = true;
  }
  let numericBracket = '';
  const numericRegex = /\([-+]?\d+(\.\d+)?\)/;
  const matchNumeric = forecast.match(numericRegex);
  if (matchNumeric) {
    numericBracket = matchNumeric[0];
    forecast = forecast.replace(numericRegex, '').trim();
  }
  let mainTranslated = '';
  const handicapRegex = /(HOME HANDICAP|AWAY HANDICAP)\s*([+-]?\d+(\.\d+)?)/i;
  const matchHandicap = forecast.match(handicapRegex);
  if (matchHandicap) {
    const textPart = matchHandicap[1].toUpperCase();
    const numPart = matchHandicap[2];
    const translatedText = t(textPart);
    mainTranslated = `${translatedText} ${numPart}`;
  } else {
    const overUnderRegex = /(OVER|UNDER)\s*([+-]?\d+(\.\d+)?)/i;
    const matchOU = forecast.match(overUnderRegex);
    if (matchOU) {
      const ouPart = matchOU[1].toUpperCase();
      const ouNum = matchOU[2];
      const translatedOU = t(ouPart);
      mainTranslated = `${translatedOU} ${ouNum}`;
    }
  }
  if (!mainTranslated) {
    if (forecast) {
      const trans = t(forecast);
      mainTranslated = trans;
    } else {
      mainTranslated = forecast;
    }
  }
  let result = mainTranslated.trim();
  if (numericBracket) {
    result += ` ${numericBracket}`;
  }
  if (hasFullTime) {
    const fullTimeTranslated = t('FULL TIME');
    result += ` (${fullTimeTranslated})`;
  }
  return result.trim();
}

const activeTab = ref('results');

const results = ref([]);
const forecast = ref([]);
const loadingResults = ref(false);
const loadingForecast = ref(false);
const errorResults = ref('');
const errorForecast = ref('');

const pageSize = 10;
const currentPageResults = ref(1);
const currentPageForecast = ref(1);

// Реактивные переменные для выбранного фильтра вида спорта
const selectedSportFilter = ref('all');
const selectedForecastSportFilter = ref('all');

function setFilter(sport) {
  if (activeTab.value === 'results') {
    selectedSportFilter.value = sport;
    currentPageResults.value = 1;
  } else {
    selectedForecastSportFilter.value = sport;
    currentPageForecast.value = 1;
  }
}

const availableSports = computed(() => {
  const sportsSet = new Set();
  results.value.forEach(item => {
    if (item.sport) {
      sportsSet.add(extractSportName(item.sport));
    }
  });
  return Array.from(sportsSet);
});

const availableForecastSports = computed(() => {
  const sportsSet = new Set();
  forecast.value.forEach(item => {
    if (item.sport) {
      sportsSet.add(extractSportName(item.sport));
    }
  });
  return Array.from(sportsSet);
});

const sortedResults = computed(() => {
  return results.value.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
});
const sortedForecast = computed(() => {
  return forecast.value.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
});

const filteredResults = computed(() => {
  if (selectedSportFilter.value === 'all') return sortedResults.value;
  return sortedResults.value.filter(item => extractSportName(item.sport) === selectedSportFilter.value);
});

const filteredForecast = computed(() => {
  if (selectedForecastSportFilter.value === 'all') return sortedForecast.value;
  return sortedForecast.value.filter(item => extractSportName(item.sport) === selectedForecastSportFilter.value);
});

const paginatedResults = computed(() => {
  const start = (currentPageResults.value - 1) * pageSize;
  return filteredResults.value.slice(start, start + pageSize);
});
const paginatedForecast = computed(() => {
  const start = (currentPageForecast.value - 1) * pageSize;
  return filteredForecast.value.slice(start, start + pageSize);
});

const totalPagesResults = computed(() => {
  return Math.ceil(filteredResults.value.length / pageSize);
});
const totalPagesForecast = computed(() => {
  return Math.ceil(filteredForecast.value.length / pageSize);
});

// Вычисляем текущую страницу и общее число страниц в зависимости от активной вкладки
const currentPage = computed(() =>
  activeTab.value === 'results' ? currentPageResults.value : currentPageForecast.value
);
const totalPages = computed(() =>
  activeTab.value === 'results' ? totalPagesResults.value : totalPagesForecast.value
);

const groupedPaginatedResults = computed(() => {
  return paginatedResults.value.reduce((groups, item) => {
    const sport = extractSportName(item.sport);
    if (!groups[sport]) {
      groups[sport] = [];
    }
    groups[sport].push(item);
    return groups;
  }, {});
});
const groupedPaginatedForecast = computed(() => {
  return paginatedForecast.value.reduce((groups, item) => {
    const sport = extractSportName(item.sport);
    if (!groups[sport]) {
      groups[sport] = [];
    }
    groups[sport].push(item);
    return groups;
  }, {});
});

const currentGroupedData = computed(() => {
  return activeTab.value === 'results'
    ? groupedPaginatedResults.value
    : groupedPaginatedForecast.value;
});

function nextPage() {
  if (activeTab.value === 'results') {
    if (currentPageResults.value < totalPagesResults.value) {
      currentPageResults.value++;
    }
  } else {
    if (currentPageForecast.value < totalPagesForecast.value) {
      currentPageForecast.value++;
    }
  }
}

function prevPage() {
  if (activeTab.value === 'results') {
    if (currentPageResults.value > 1) {
      currentPageResults.value--;
    }
  } else {
    if (currentPageForecast.value > 1) {
      currentPageForecast.value--;
    }
  }
}

function extractSportName(fullSportName) {
  if (!fullSportName) return 'other';
  return fullSportName.split('_')[0] || 'other';
}

function formatSport(name) {
  return sportNames[name] || name.charAt(0).toUpperCase() + name.slice(1);
}

function getIcon(sport) {
  const sportName = sport ? sport.split('_')[0].toLowerCase() : 'other';
  const iconMap = {
    'american football': 'https://img.icons8.com/?size=100&id=9775&format=png&color=000000',
    'nrl': 'https://img.icons8.com/?size=100&id=9775&format=png&color=000000',
    'baseball': 'https://img.icons8.com/?size=100&id=60038&format=png&color=000000',
    'basketball': 'https://img.icons8.com/?size=100&id=fCa2W359XPO3&format=png&color=000000',
    'golf': 'https://img.icons8.com/?size=100&id=60447&format=png&color=000000',
    'icehockey': 'https://img.icons8.com/?size=100&id=pg0oNK0iG0gx&format=png&color=000000',
    'rugby league': 'https://img.icons8.com/?size=100&id=9775&format=png&color=000000',
    'rugby union': 'https://img.icons8.com/?size=100&id=9775&format=png&color=000000',
    'rugbyleague': 'https://img.icons8.com/?size=100&id=9775&format=png&color=000000',
    'soccer': 'https://img.icons8.com/?size=100&id=61032&format=png&color=000000',
    'tennis': 'https://img.icons8.com/?size=100&id=62451&format=png&color=000000',
    'mma': 'https://img.icons8.com/?size=100&id=Q47LCMLRs42t&format=png&color=000000',
    'boxing': 'https://img.icons8.com/?size=100&id=566&format=png&color=000000',
    'cricket': 'https://img.icons8.com/?size=100&id=56061&format=png&color=000000',
    'formula 1': 'https://img.icons8.com/?size=100&id=62306&format=png&color=000000',
    'motorsport': 'https://img.icons8.com/?size=100&id=62819&format=png&color=000000',
    'cycling': 'https://img.icons8.com/?size=100&id=50155&format=png&color=000000',
    'esports': 'https://img.icons8.com/?size=100&id=59791&format=png&color=000000',
    'esports_dota': 'https://img.icons8.com/?size=100&id=61898&format=png&color=000000'
  };

  if (sport === "esports_csgo") {
    return "https://img.icons8.com/?size=100&id=0zCSPyEKiaTL&format=png&color=000000";
  }
  if (sport === "esports_dota") {
    return "https://img.icons8.com/?size=100&id=61898&format=png&color=000000";
  }
  return iconMap[sportName] || null;
}

function getCoefficientClass(item) {
  if (activeTab.value === 'results') {
    return item.betResult === 'won'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  } else {
    return 'bg-blue-100 text-blue-800';
  }
}

function formatDateCompact(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month} ${hours}:${minutes}`;
}

onMounted(() => {
  fetchResults();
  fetchForecast();
});

async function fetchResults() {
  loadingResults.value = true;
  errorResults.value = '';
  try {
    const response = await fetch('https://gallbon.com/api/results');
    if (!response.ok) {
      throw new Error('Error fetching results from server');
    }
    const data = await response.json();
    results.value = Array.isArray(data) ? data : [];
  } catch (err) {
    errorResults.value = err.message;
  } finally {
    loadingResults.value = false;
  }
}

async function fetchForecast() {
  loadingForecast.value = true;
  errorForecast.value = '';
  try {
    const response = await fetch('https://gallbon.com/api/analysis');
    if (!response.ok) {
      throw new Error('Error fetching forecast from server');
    }
    const data = await response.json();
    forecast.value = Array.isArray(data) ? data : [];
  } catch (err) {
    errorForecast.value = err.message;
  } finally {
    loadingForecast.value = false;
  }
}
</script>

<style>
@media (prefers-color-scheme: dark) {
  :root {
    color-scheme: light !important;
  }
}
</style>
